<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card-text>
      <v-card class="mx-auto" elevation="0" title="Demographic baseline numbers"
              ref="demographic-baseline-numbers">
        <v-card-subtitle>Use the information icon, in the top right corner, to see the population definition, and
          use the filter icon to filter the cohort.</v-card-subtitle>
        <v-divider :vertical="true"></v-divider>
        <v-card-subtitle class="no-dim text-primary">Total</v-card-subtitle>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="demographicBaselineTotalNumberOfPatients.value"
                        :label="demographicBaselineTotalNumberOfPatients.label"
                        :comment="demographicBaselineTotalNumberOfPatients.comment"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="demographicBaselineAgeChartData"
                                          :chart-comment="demographicBaselineAgeChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="demographicBaselineAgeNumbers"
                                          :table-comment="demographicBaselineAgeComment"
                                          :visible="visible"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="demographicBaselineSexChartData"
                                          :chart-comment="demographicBaselineSexChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="demographicBaselineSexNumbers"
                                          :table-comment="demographicBaselineSexComment"
                                          :visible="visible"/>
          </v-col>
        </v-row>
        <v-card-subtitle class="no-dim text-primary">Filtered</v-card-subtitle>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <number-box :number="filteredDemographicBaselineTotalNumberOfPatients.value"
                        :label="filteredDemographicBaselineTotalNumberOfPatients.label"
                        :comment="filteredDemographicBaselineTotalNumberOfPatients.comment"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="filteredDemographicBaselineAgeChartData"
                                          :chart-comment="filteredDemographicBaselineAgeChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="filteredDemographicBaselineAgeNumbers"
                                          :table-comment="filteredDemographicBaselineAgeComment"
                                          :visible="visible"
                                          v-on:itemSelected="filteredDemographicBaselineChartItemSelected($event)"/>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <combined-pie-chart-and-table :chart-data="filteredDemographicBaselineSexChartData"
                                          :chart-comment="filteredDemographicBaselineSexChartComment"
                                          :table-headers="demographicBaselineNumbersHeaders"
                                          :table-data="filteredDemographicBaselineSexNumbers"
                                          :table-comment="filteredDemographicBaselineSexComment"
                                          :visible="visible"
                                          v-on:itemSelected="filteredDemographicBaselineChartItemSelected($event)"/>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-right">
            <vue-to-print :content="() => this.$refs['demographic-baseline-numbers'].$el"
                          :document-title="icd + '-' + 'demographic-baseline-numbers'"
                          remove-after-print>
              <template v-slot:trigger>
                <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
              </template>
            </vue-to-print>
          </v-col>
        </v-row>
      </v-card>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Filtered number of new cases"
                  ref="number-of-diagnosed-patients">
            <v-card-subtitle class="text-wrap">{{ totalNumberOfPatientsPerYearChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart"
                        @ready="totalNumberOfPatientsPerYearChartReady"
                        :data="totalNumberOfPatientsPerYearChartData"
                        :options="totalNumberOfPatientsPerYearChartOptions"
                        :key="'totalNumberOfPatientsPerYearChart' + totalNumberOfPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-diagnosed-patients'].$el"
                              :document-title="icd + '-' + 'number-of-diagnosed-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Filtered number of prevalent patients"
                  ref="number-of-prevalent-patients">
            <v-card-subtitle class="text-wrap">{{ totalNumberOfPrevalentPatientsPerYearChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="totalNumberOfPrevalentPatientsPerYearChartData"
                        :options="totalNumberOfPrevalentPatientsPerYearChartOptions"
                        :key="'totalNumberOfPrevalentPatientsPerYearChart' + totalNumberOfPrevalentPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfPrevalentPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-prevalent-patients'].$el"
                              :document-title="icd + '-' + 'number-of-prevalent-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Filtered number of deaths"
                  ref="number-of-deceased-patients">
            <v-card-subtitle class="text-wrap">{{ totalNumberOfDeceasedPatientsPerYearChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="totalNumberOfDeceasedPatientsPerYearChartData"
                        :options="totalNumberOfDeceasedPatientsPerYearChartOptions"
                        :key="'totalNumberOfDeceasedPatientsPerYearChart' + totalNumberOfDeceasedPatientsPerYearChartKeyIndex"
                        v-if="totalNumberOfDeceasedPatientsPerYearChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-deceased-patients'].$el"
                              :document-title="icd + '-' + 'number-of-deceased-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Incidence proportion per 100,000"
                  ref="incidence-rate-per-100000">
            <v-card-subtitle class="text-wrap">{{ incidenceRatePer100000ChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="incidenceRatePer100000ChartData"
                        :options="incidenceRatePer100000ChartOptions"
                        :key="'incidenceRatePer100000Chart' + incidenceRatePer100000ChartKeyIndex"
                        v-if="incidenceRatePer100000ChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Point prevalence per 100,000"
                  ref="period-prevalence-per-100000">
            <v-card-subtitle class="text-wrap">{{ periodPrevalencePer100000ChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="periodPrevalencePer100000ChartData"
                        :options="periodPrevalencePer100000ChartOptions"
                        :key="'periodPrevalencePer100000Chart' + periodPrevalencePer100000ChartKeyIndex"
                        v-if="periodPrevalencePer100000ChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="All-cause mortality among all cases per 100"
                  ref="all-cause-mortality-per-100">
            <v-card-subtitle class="text-wrap">{{ allCauseMortalityPer100ChartSubtitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="allCauseMortalityPer100ChartData"
                        :options="allCauseMortalityPer100ChartOptions"
                        :key="'allCauseMortalityPer100Chart' + allCauseMortalityPer100ChartKeyIndex"
                        v-if="allCauseMortalityPer100ChartData.length !== 0"/>
                <v-progress-circular color="primary" indeterminate v-else></v-progress-circular>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Geographical region"
                  ref="geographical-region-summary">
            <v-card-subtitle class="text-wrap">Patient distribution by Swedish region. These values are for the total cohort and selected filters are not applied here.</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="geographicalRegionHeaders"
                              :items="geographicalRegionItems"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['geographical-region-summary'].$el"
                              :document-title="icd + '-' + 'geographical-region-summary'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Aggregated statistics"
                  ref="aggregated-statistics">
            <v-card-subtitle>These values are for the total cohort and selected filters are not applied here.</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="aggregatedStatisticsHeaders"
                              :items="aggregatedStatisticsData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['aggregated-statistics'].$el"
                              :document-title="icd + '-' + 'aggregated-statistics'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Comorbidities"
                  ref="most-common-icd-chapters">
            <v-card-subtitle class="text-wrap">Aggregated statistics for the most common ICD10 chapters reported in the
              Patient Register (in/outpatient, primary or secondary diagnosis) between 1 Jan 2000 - 31 Dec 2024,
              counting AFTER index date. These values are for the total cohort and selected filters are not applied here.
            </v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="mostCommonIcdChapterHeadersForDataset"
                              :items="mostCommonIcdChapterItems"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['most-common-icd-chapters'].$el"
                              :document-title="icd + '-' + 'most-common-icd-chapters'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";
import CombinedPieChartAndTable from "@/components/CombinedPieChartAndTable.vue";
import NumberBox from "@/components/NumberBox.vue";

export default defineComponent({
  name: 'NeurologicalDiseaseDemographicBaseline',
  props: {
    icd: String,
    diseaseDescription: String,
    ageAtDiagnosis: Array,
    sex: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    visible: Boolean
  },
  components: {
    NumberBox,
    GChart,
    CombinedPieChartAndTable,
    VueToPrint
  },
  data: context => ({
    loading: false,
    demographicBaselineTotalNumberOfPatients: {},
    demographicBaselineNumbersHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Value',
        align: 'end',
        key: 'value',
      },
      {
        title: 'Percentage',
        align: 'end',
        key: 'percentage',
      }],
    demographicBaselineAgeNumbers: [],
    demographicBaselineAgeComment: '',
    demographicBaselineSexNumbers: [],
    demographicBaselineSexComment: '',
    demographicBaselineAgeChartData: [],
    demographicBaselineAgeChartComment: '',
    demographicBaselineSexChartData: [],
    demographicBaselineSexChartComment: '',
    aggregatedStatisticsHeaders: [
      {
        title: 'Statistic',
        align: 'start',
        key: 'statistic_name',
      },
      {
        title: 'Value',
        align: 'start',
        key: 'statistic',
      },
      {
        title: 'Description',
        align: 'start',
        key: 'statistic_description',
      }
    ],
    aggregatedStatisticsData: [],
    geographicalRegionHeaders: [
      {
        title: 'Region',
        align: 'start',
        key: 'region',
        sortable: false
      },
      {
        title: 'Number of patients',
        align: 'start',
        key: 'numberOfPatients',
        sortable: false
      },
      {
        title: 'Percentage among total number of patients',
        align: 'start',
        key: 'percentage',
        sortable: false
      }
    ],
    geographicalRegionItems: [],
    mostCommonIcdChapterHeaders: [
      {
        title: 'ICD10 chapter',
        align: 'start',
        key: 'icdChapter',
        sortable: false
      },
      {
        title: 'Description',
        align: 'start',
        key: 'icdChapterDescription',
        sortable: false
      },
      {
        title: 'Number of patients receiving diagnosis',
        align: 'start',
        key: 'numberOfPatients',
        sortable: false
      },
      {
        title: 'Percentage among total number of patients',
        align: 'start',
        key: 'percentage',
        sortable: false
      }
    ],
    mostCommonIcdChapterHeadersCompact: [
      {
        title: 'ICD-10 chapter',
        align: 'start',
        key: 'icdChapter',
        sortable: false
      },
      {
        title: 'Description',
        align: 'start',
        key: 'icdChapterDescription',
        sortable: false
      },
      {
        title: 'Number of patients (%)',
        align: 'start',
        key: 'numberOfPatientsAndPercentage',
        sortable: false
      }
    ],
    mostCommonIcdChapterItems: [],
    filteredDemographicBaselineTotalNumberOfPatients: {},
    filteredDemographicBaselineAgeNumbers: [],
    filteredDemographicBaselineAgeComment: '',
    filteredDemographicBaselineSexNumbers: [],
    filteredDemographicBaselineSexComment: '',
    selectedFilteredDemographicBaseline: 'filteredDemographicBaselinePieChartValue',
    filteredDemographicBaselineAgeChartData: [],
    filteredDemographicBaselineAgeChartComment: '',
    filteredDemographicBaselineSexChartData: [],
    filteredDemographicBaselineSexChartComment: '',
    totalNumberOfPatientsPerYearChartKeyIndex: 0,
    totalNumberOfPatientsPerYearChartSubtitle: 'The diagnosis date is defined as the date of ' +
        context.diseaseDescription + ' first recorded for the patient in the Patient Register.',
    totalNumberOfPatientsPerYearChartData: [],
    totalNumberOfPatientsPerYearChartOptions: {
      title: 'New cases per year',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#b2df8a'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    totalNumberOfPrevalentPatientsPerYearChartKeyIndex: 0,
    totalNumberOfPrevalentPatientsPerYearChartSubtitle: '',
    totalNumberOfPrevalentPatientsPerYearChartData: [],
    totalNumberOfPrevalentPatientsPerYearChartOptions: {
      title: 'Prevalent patients per year',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#fdbf6f'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    totalNumberOfDeceasedPatientsPerYearChartKeyIndex: 0,
    totalNumberOfDeceasedPatientsPerYearChartSubtitle: 'Death dates are computed given the number of days from the ' +
        'index date rounded to the nearest ten.',
    totalNumberOfDeceasedPatientsPerYearChartData: [],
    totalNumberOfDeceasedPatientsPerYearChartOptions: {
      title: 'Deaths per year',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#fb9a99'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    incidenceRatePer100000ChartKeyIndex: 0,
    incidenceRatePer100000ChartSubtitle: 'These rates are loaded from the epidemiology rates workbook and are not filtered by cohort selections.',
    incidenceRatePer100000ChartData: [],
    incidenceRatePer100000ChartOptions: {
      title: 'Incidence proportion per 100,000',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#80b1d3'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    periodPrevalencePer100000ChartKeyIndex: 0,
    periodPrevalencePer100000ChartSubtitle: 'These rates are loaded from the epidemiology rates workbook and are not filtered by cohort selections.',
    periodPrevalencePer100000ChartData: [],
    periodPrevalencePer100000ChartOptions: {
      title: 'Point prevalence per 100,000',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#fdb462'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    allCauseMortalityPer100ChartKeyIndex: 0,
    allCauseMortalityPer100ChartSubtitle: 'These rates are loaded from the epidemiology rates workbook and are not filtered by cohort selections.',
    allCauseMortalityPer100ChartData: [],
    allCauseMortalityPer100ChartOptions: {
      title: 'All-cause mortality among all cases per 100',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#fb8072'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      annotations: {
        textStyle: {
          fontSize: 12,
          color: '#808080',
          auraColor: 'none'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    }
  }),
  computed: {
    mostCommonIcdChapterHeadersForDataset() {
      return this.mostCommonIcdChapterHeadersCompact;
    }
  },
  methods: {
    forceRerender() {
      this.totalNumberOfPatientsPerYearChartKeyIndex += 1;
      this.totalNumberOfDeceasedPatientsPerYearChartKeyIndex += 1;
      this.totalNumberOfPrevalentPatientsPerYearChartKeyIndex += 1;
      this.incidenceRatePer100000ChartKeyIndex += 1;
      this.periodPrevalencePer100000ChartKeyIndex += 1;
      this.allCauseMortalityPer100ChartKeyIndex += 1;
    },
    async loadDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('neurological-disease/demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalNumberOfPatients = response.data;
      });
    },
    async loadDemographicBaselineData() {
      let ageData = axios.get('neurological-disease/demographic-baseline-age',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      let sexData = axios.get('neurological-disease/demographic-baseline-sex',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return await Promise.all([ageData, sexData]).then((responses) => {
        this.demographicBaselineAgeNumbers = responses[0].data.data;
        this.demographicBaselineAgeComment = responses[0].data.comment;
        this.demographicBaselineSexNumbers = responses[1].data.data;
        this.demographicBaselineSexComment = responses[1].data.comment;
      });
    },
    async loadDemographicBaselineChartData() {
      let ageChartData = axios.get('neurological-disease/demographic-baseline-age-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      let sexChartData = axios.get('neurological-disease/demographic-baseline-sex-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return await Promise.all([ageChartData, sexChartData]).then((responses) => {
        this.demographicBaselineAgeChartData = responses[0].data.data
        this.demographicBaselineAgeChartComment = responses[0].data.comment
        this.demographicBaselineSexChartData = responses[1].data.data;
        this.demographicBaselineSexChartComment = responses[1].data.comment;
      });
    },
    async loadAggregatedStatistics() {
      let data = axios.get('neurological-disease/aggregated-statistics',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.aggregatedStatisticsData = response.data
      });
    },
    async loadMostCommonIcdChaptersData() {
      let data = axios.get('neurological-disease/most-common-icd-chapters',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.mostCommonIcdChapterItems = response.data
      })
    },
    async loadGeographicalRegionSummaryData() {
      let data = axios.get('neurological-disease/geographical-region-summary',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.geographicalRegionItems = response.data
      });
    },
    async loadFilteredDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('neurological-disease/filtered-demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      return data.then((response) => {
        this.filteredDemographicBaselineTotalNumberOfPatients = response.data
      })
    },
    async loadFilteredDemographicBaselineData() {
      let ageData = axios.get('neurological-disease/filtered-demographic-baseline-age',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      let sexData = axios.get('neurological-disease/filtered-demographic-baseline-sex',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      return await Promise.all([ageData, sexData]).then((responses) => {
        this.filteredDemographicBaselineAgeNumbers = responses[0].data.data
        this.filteredDemographicBaselineAgeComment = responses[0].data.comment
        this.filteredDemographicBaselineSexNumbers = responses[1].data.data
        this.filteredDemographicBaselineSexComment = responses[1].data.comment
      })
    },
    async loadFilteredDemographicBaselineChartData() {
      let ageData = axios.get('neurological-disease/filtered-demographic-baseline-age-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      let sexData = axios.get('neurological-disease/filtered-demographic-baseline-sex-chart',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      return await Promise.all([ageData, sexData]).then((responses) => {
        this.filteredDemographicBaselineAgeChartData = responses[0].data.data
        this.filteredDemographicBaselineAgeChartComment = responses[0].data.comment
        this.filteredDemographicBaselineSexChartData = responses[1].data.data
        this.filteredDemographicBaselineSexChartComment = responses[1].data.comment
      });
    },
    filteredDemographicBaselineChartItemSelected(item) {
      if (item?.age_group !== undefined) {
        this.$emit('ageGroupSelected', item.age_group)
      }
      if (item?.sex !== undefined) {
        this.$emit('sexSelected', item.sex)
      }
    },
    async loadTotalNumberOfPatientsPerYearChartData() {
      let data = axios.get('neurological-disease/total-number-of-patients-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      return data.then((response) => {
        this.totalNumberOfPatientsPerYearChartData = response.data
      })
    },
    async loadTotalNumberOfDeceasedPatientsPerYearChartData() {
      let data = axios.get('neurological-disease/total-number-of-deceased-patients-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      return data.then((response) => {
        this.totalNumberOfDeceasedPatientsPerYearChartData = response.data
      })
    },
    async loadTotalNumberOfPrevalentPatientsPerYearChartData() {
      let data = axios.get('neurological-disease/total-number-of-prevalent-patients-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
      return data.then((response) => {
        this.totalNumberOfPrevalentPatientsPerYearChartData = response.data
      })
    },
    async loadIncidenceRatePer100000ChartData() {
      let data = axios.get('neurological-disease/incidence-proportion-per-100000-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.incidenceRatePer100000ChartData = response.data
      })
    },
    async loadPeriodPrevalencePer100000ChartData() {
      let data = axios.get('neurological-disease/period-prevalence-per-100000-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.periodPrevalencePer100000ChartData = response.data
      })
    },
    async loadAllCauseMortalityPer100ChartData() {
      let data = axios.get('neurological-disease/all-cause-mortality-among-all-cases-per-100-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
      return data.then((response) => {
        this.allCauseMortalityPer100ChartData = response.data
      })
    },
    async totalNumberOfPatientsPerYearChartReady(chart, google) {
      const setTotalNumberOfPatientsPerYearChartSelection = (selectedRow) => {
        let selectedYear = this.totalNumberOfPatientsPerYearChartData[selectedRow + 1][0]
        if (selectedYear !== undefined) {
          this.$emit('diagnosisYearSelected', selectedYear)
        }
      }

      const totalNumberOfPatientsPerYearSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setTotalNumberOfPatientsPerYearChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', totalNumberOfPatientsPerYearSelectHandler);
    },
    async loadDynamicData() {
      const requests = [
        this.loadFilteredDemographicBaselineTotalNumberOfPatientsData(),
        this.loadFilteredDemographicBaselineData(),
        this.loadFilteredDemographicBaselineChartData(),
        this.loadTotalNumberOfPatientsPerYearChartData(),
        this.loadTotalNumberOfDeceasedPatientsPerYearChartData(),
        this.loadTotalNumberOfPrevalentPatientsPerYearChartData(),
        this.loadIncidenceRatePer100000ChartData(),
        this.loadPeriodPrevalencePer100000ChartData(),
        this.loadAllCauseMortalityPer100ChartData()
      ];
      return await Promise.all(requests).then(() => {
        console.log('Dynamic data loaded: NeurologicalDiseaseDemographicBaseline')
      })
    }
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.loadDemographicBaselineTotalNumberOfPatientsData(),
      this.loadDemographicBaselineData(),
      this.loadDemographicBaselineChartData(),
      this.loadGeographicalRegionSummaryData(),
      this.loadAggregatedStatistics(),
      this.loadMostCommonIcdChaptersData()
    ]).then(() => {
      this.loadDynamicData()
    }).finally(() => {
      this.loading = false;
    });
  },
  updated() {
    if (this.visible) {
      this.loading = true;
      this.loadDynamicData()
          .then(() => this.forceRerender())
          .finally(() => {
            this.loading = false;
          });
    }
  }
});
</script>

<style scoped>
.striped-table :deep(tbody tr:nth-child(odd)) {
  background-color: rgba(0, 0, 0, .10);
}

.no-dim { opacity: 1 !important; } /* cancels Vuetify's dimming */

@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
